import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Instagram, Facebook, ArrowUpRight, Sparkles } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Wordmark } from '@/components/Logo';

const Footer: React.FC = () => {
  const { t } = useLanguage();

  return (
    <footer className="relative bg-background border-t border-border/60 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.25]" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 h-72 w-[42rem] rounded-full bg-primary/[0.10] blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Lead mark */}
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-16">
          <div className="col-span-12 md:col-span-5 lg:col-span-6">
            <Link to="/" className="inline-block mb-6" aria-label="Spawncore — Home">
              <Wordmark size="lg" />
            </Link>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-md">
              {t('footer.tagline')}
            </p>
            <p className="mt-5 font-mono-tag text-xs uppercase tracking-[0.24em] text-lime/80 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              {t('footer.based')}
            </p>
          </div>

          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <h3 className="eyebrow mb-5">— {t('footer.services')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/services/ai" className="text-foreground hover:text-lime smooth-transition flex items-center gap-2">
                  {t('footer.servicesMenu.ai')}
                  <span className="font-mono-tag text-[9px] uppercase tracking-[0.24em] text-lime/80">·new</span>
                </Link>
              </li>
              <li>
                <Link to="/services/web" className="text-muted-foreground hover:text-foreground smooth-transition">
                  {t('footer.servicesMenu.web')}
                </Link>
              </li>
              <li>
                <Link to="/services/social" className="text-muted-foreground hover:text-foreground smooth-transition">
                  {t('footer.servicesMenu.social')}
                </Link>
              </li>
              <li>
                <Link to="/services/seo" className="text-muted-foreground hover:text-foreground smooth-transition">
                  {t('footer.servicesMenu.seo')}
                </Link>
              </li>
              <li>
                <Link to="/services/training" className="text-muted-foreground hover:text-foreground smooth-transition">
                  {t('footer.servicesMenu.training')}
                </Link>
              </li>
              <li>
                <Link to="/services/dpo" className="text-muted-foreground hover:text-foreground smooth-transition">
                  {t('footer.servicesMenu.dpo')}
                </Link>
              </li>
            </ul>
          </div>

          <div className="col-span-6 md:col-span-4 lg:col-span-4">
            <h3 className="eyebrow mb-5">— {t('footer.contact')}</h3>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3 text-muted-foreground">
                <MapPin className="h-4 w-4 mt-1 text-lime flex-shrink-0" strokeWidth={1.8} />
                <span>Largo do Esteiro 6<br />2050-261 Azambuja, PT</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-lime flex-shrink-0" strokeWidth={1.8} />
                <a
                  href="mailto:joao@spawncore.com"
                  className="text-foreground hover:text-lime smooth-transition"
                >
                  joao@spawncore.com
                </a>
              </li>
            </ul>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/spawncore.agency/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-lime hover:border-lime/40 smooth-transition"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61579331546296"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-lime hover:border-lime/40 smooth-transition"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="relative overflow-hidden mb-8 select-none" aria-hidden="true">
          <div className="font-brand text-[clamp(4rem,18vw,16rem)] leading-[0.85] text-foreground/[0.07] whitespace-nowrap">
            spawncore.
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-8 border-t border-border/60">
          <p className="font-mono-tag text-xs uppercase tracking-[0.24em] text-muted-foreground">
            © {new Date().getFullYear()} Spawncore. {t('footer.rights')}
          </p>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground smooth-transition flex items-center gap-1.5">
              {t('footer.privacy')}
              <ArrowUpRight className="h-3 w-3" />
            </Link>
            <Link to="/contact" className="hover:text-foreground smooth-transition flex items-center gap-1.5">
              {t('footer.contact')}
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
